import React, { useState } from 'react';
import {
    Sparkles, ArrowRight, Zap, Globe, DollarSign, MessageSquare, Calendar, MessageCircle, Layout, Search, Share2, Video,
    Users, GraduationCap, HeartHandshake, BookOpen, TrendingUp, Heart, FileQuestion, User, Phone, ChevronUp
} from 'lucide-react';
import CardsCarousel from './components/card-carousel';
import { Img } from 'components';
import { FlipText } from '../HeroSection';
import Header from './components/Header';

export const optionsforYourAiEmployee = [
    {
        id: 'digital-marketing',
        name: 'Digital Marketing',
        options: [
            { icon: Layout, text: 'Instant Responsive Website Developer', badge: null },
            { icon: Search, text: 'SEO Specialist', badge: null },
            { icon: Share2, text: 'Social Media Manager', badge: null },
            { icon: Video, text: 'Automated Reels & Video Generator', badge: null },
            { icon: Users, text: '24/7 Virtual Admission Counsellor', badge: null },
        ]
    },
    {
        id: 'teachers',
        name: 'Teachers',
        options: [
            { icon: GraduationCap, text: 'SuperStudent', badge: 'Coming soon' },
            { icon: HeartHandshake, text: 'Parent Helpdesk', badge: null },
            { icon: BookOpen, text: 'Child Care', badge: null },
            { icon: TrendingUp, text: '1st Rank', badge: null },
        ]
    },
    {
        id: 'operations',
        name: 'Operations',
        options: [
            { icon: Globe, text: 'Multilingual Communication', badge: null },
            { icon: DollarSign, text: 'Auto Fee Collection', badge: null },
            { icon: MessageSquare, text: 'Parental Feedback', badge: null },
            { icon: Calendar, text: 'AI-Daily Tasks', badge: null },
            { icon: MessageCircle, text: 'AI-Chat', badge: null },
        ]
    },
    {
        id: 'self-healing-support',
        name: 'Self-Healing Support',
        options: [
            { icon: Heart, text: 'Self Healer', badge: 'New' },
            { icon: FileQuestion, text: 'Record and Report', badge: null },
            { icon: User, text: 'Virtual Relationship Manager', badge: null },
            { icon: Phone, text: 'AI-WhatsApp Support', badge: null },
        ]
    }
];

const HeroTitleText = [
    {
        text: "Schools"
    }, {
        text: "Colleges"
    }
]
interface NewHeroSectionProps {
    renderComponents?: any[];
    sectionRefs?: React.MutableRefObject<any[]>;
}

export default function NewHeroSection({ renderComponents, sectionRefs }: NewHeroSectionProps) {

    const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    React.useEffect(() => {
        const toggleVisibility = () => {
            if (window.pageYOffset > 300) {
                setIsVisible(() => true);
            } else {
                setIsVisible(() => false);
            }
        };

        window.addEventListener('scroll', toggleVisibility);

        return () => window.removeEventListener('scroll', toggleVisibility);
    }, []);

    React.useEffect(() => {
        let timeoutId;

        const cycleFeature = () => {
            setCurrentFeatureIndex((prev) => (prev + 1) % HeroTitleText.length);
            timeoutId = setTimeout(cycleFeature, 3000); // call again after 3 seconds
        };

        // start the first cycle
        timeoutId = setTimeout(cycleFeature, 3000);

        return () => clearTimeout(timeoutId); // cleanup on unmount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [HeroTitleText.length]);

    return (
        <div className="relative mx-auto">
            <Header
                sectionRefs={sectionRefs}
            />

            {/* Main Content */}
            <main className="px-12">
                {/* Hero Text */}
                <div className="flex justify-center mb-[1vh] md:hidden">
                    <button className="flex items-center gap-2 bg-[#F5F5FF] border-[1px] border-[#4949DE] text-[#4949DE] px-[2vh] py-[1vh] rounded-full font-semibold text-[1.6vh] md:text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        <Sparkles className='h-4' /> Funded and Collaborated by
                    </button>
                </div>
                <div className="flex justify-center">
                    <div className="container flex flex-col items-center text-center mb-[2.5vh] space-y-2">
                        <div className='relative text-center flex flex-col items-center md:hidden p-0 m-0'>
                            <h1 className="relative 
                                w-fit 
                                text-[7vh] md:text-3xl 
                                font-bold 
                                text-[#181818] 
                                font-inter 
                                not-italic 
                                leading-[100%] 
                                tracking-[-0.96px] m-0 p-0" style={{ fontFamily: 'Inter, sans-serif' }}>
                                <div className="md:hidden absolute top-[-2vh] -left-[18vh]"><Img enableShimmer={false} src={"/images/new-landing-page/studentsArrow.svg"} className='w-[14vh]' /></div>
                                <div className="md:hidden absolute top-[-1vh] -right-[18vh]"><Img enableShimmer={false} src={"/images/new-landing-page/staffArrow.svg"} className='w-[14vh]' /></div>
                                <span className="text-[#EB4335]">India's</span>{' '}
                                <span className="text-gray-900">First AI-Powered</span>
                            </h1>
                            <h1 className="relative 
                                w-fit 
                                text-[7vh] md:text-3xl 
                                font-bold 
                                text-[#181818] 
                                font-inter 
                                not-italic 
                                leading-[100%] 
                                tracking-[-0.96px] -mt-[1vh] p-0">
                                <span className="text-gray-900">Business Ecosystem for </span>
                                <FlipText key={currentFeatureIndex}>
                                    <span className="text-[#8431D6]">
                                        {HeroTitleText[currentFeatureIndex]?.text}
                                    </span>
                                </FlipText>
                            </h1>
                        </div>
                        <div className="hidden md:block">
                            <h1 className="text-[7vh] md:text-3xl leading-tight font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>
                                <span className="text-orange-500">India's</span>{' '}
                                First AI-Powered Business Ecosystem for <span className="text-[#8431D6]">Schools</span>
                            </h1>
                        </div>
                        <div className="relative w-fit">
                            <h2 className="text-center text-[2.5vh] font-medium text-[#4C4C4C] md:text-lg" style={{ fontFamily: 'Instrument Sans, sans-serif' }}>
                                20 AI employees- No Sleep. No Limits. Just Success
                            </h2>
                            <div className="md:hidden absolute bottom-[-4vh] -left-40"><Img enableShimmer={false} src={"/images/new-landing-page/parentArrow.svg"} className='w-[14vh]' /></div>
                            <div className="md:hidden absolute bottom-[-4vh] -right-40"><Img enableShimmer={false} src={"/images/new-landing-page/teacherArrow.svg"} className='w-[17vh]' /></div>
                        </div>
                    </div>
                </div>

                <CardsCarousel
                    renderComponents={renderComponents}
                    sectionRefs={sectionRefs}
                    scrollUp={scrollToTop}
                />

                {/* CTA Section */}
                <div className="text-center mt-[5.4vh]">
                    <div className="flex items-center justify-center gap-6 md:flex-col md:justify-center">
                        <button className="relative flex items-center gap-2 bg-gradient-to-r from-[#E5542F] to-[#8431D6] text-[white] px-[2.5vh] py-[1.5vh] rounded-full font-semibold text-[1.8vh] shadow-lg md:text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                            <span>Join Customized Webinar</span> <ArrowRight className='h-4' />
                        </button>
                        <button className="flex items-center gap-2 border-3 border-blue-600 text-blue-600 px-[2.5vh] py-[1.2vh] rounded-full font-semibold text-[1.8vh] md:text-sm" style={{ fontFamily: 'Poppins, sans-serif', borderWidth: '3px' }}>
                            <Zap className='h-4' /> <span>Activate Within 10 Minutes</span>
                        </button>
                    </div>
                </div>
            </main>
            {isVisible && (
                <button
                    onClick={scrollToTop}
                    className="fixed bottom-8 right-8 bg-blue-600 hover:bg-blue-700 text-[white] p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 z-50"
                    aria-label="Scroll to top"
                    type='button'
                >
                    <ChevronUp size={24} />
                </button>
            )}
        </div>
    );
}